<template>
	<form class="main-wrapper mt-4 mb-3" @submit.prevent="handleSubmit">
		<OnSiteMessageGeneral
			:id="id"
			ref="general"
			name-label="Campaign name*"
			:name.sync="name"
			:status.sync="status"
			:duration.sync="duration"
			:session-based.sync="sessionBased"
		/>

		<OnSiteMessageDate
			ref="date"
			:status="status"
			:start-date.sync="startDate"
			:start-time.sync="startTime"
			:end-date.sync="endDate"
			:end-time.sync="endTime"
		/>

		<!-- TODO: update preview image -->
		<OnSiteMessageContentWrapper
			ref="content"
			desktop-img="/assets/images/small-banner-desktop.svg"
			mobile-img="/assets/images/small-banner-mobile.svg"
			:campaign-link.sync="campaignLink"
		>
			<template #title>
				Popup Image
			</template>

			<BaseMediaSelectorBox
				ref="desktopImage"
				v-model="desktopImage"
				title="Desktop Image"
				no-image-text="No desktop Image"
				show-helper
				width="748"
				height="562"
				class="mt-4"
				:required="isRequiredImage"
			/>

			<BaseMediaSelectorBox
				ref="mobileImage"
				v-model="mobileImage"
				title="Mobile Image"
				no-image-text="No mobile Image"
				show-helper
				width="310"
				height="306"
				class="mt-4"
				:required="isRequiredImage"
			/>
		</OnSiteMessageContentWrapper>

		<BaseActionPanelStickyFooter
			:disabled-confirm="isLoading || isUpdating"
			:is-edit="isEditMode"
			:remove-text="isEditMode ? 'Remove campaign' : null"
			@onConfirm="handleSubmit"
			@onCancel="$router.push({ name: ROUTE_NAME.ON_SITE_MESSAGE_POPUP_LIST})"
			@onRemove="isEditMode ? $refs['modal-remove'].open() : null"
		/>

		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="deletePopupCampaign.bind(null, id)"
			title="Remove this campaign?"
			description="By removing this, this campaign info will be disappeared from the list."
			@onSuccess="$router.push({ name: ROUTE_NAME.ON_SITE_MESSAGE_POPUP_LIST})"
		/>
	</form>
</template>

<script>
import { mapActions } from 'vuex';

import OnSiteMessageGeneral from '@/components/OnSiteMessageGeneral.vue';
import OnSiteMessageDate from '@/components/OnSiteMessageDate.vue';
import OnSiteMessageContentWrapper from '@/components/OnSiteMessageContentWrapper.vue';
import BaseMediaSelectorBox from '@/components/BaseMediaSelectorBox.vue';

import { convertDateTimeToUTC } from '../assets/js/helpers';
import { ROUTE_NAME } from '../enums/route';

export default {
	name: 'OnSiteMessagePopupForm',

	components: {
		OnSiteMessageGeneral,
		OnSiteMessageDate,
		OnSiteMessageContentWrapper,
		BaseMediaSelectorBox,
	},

	props: {
		isEditMode: {
			type: Boolean,
			default: false,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		isUpdating: {
			type: Boolean,
			default: false,
		},
		data: {
			type: Object,
			default: null,
		},
	},

	data() {
		return {
			ROUTE_NAME,

			id: null,
			name: '',
			duration: 0,
			status: true,
			sessionBased: false,

			startDate: null,
			startTime: '00:00',
			endDate: null,
			endTime: '23:59',

			desktopImage: null,
			mobileImage: null,
			campaignLink: null,
		};
	},

	computed: {
		isRequiredImage() {
			return !this.desktopImage && !this.mobileImage;
		},
	},

	watch: {
		data: {
			immediate: true,
			handler(value) {
				if (this.isEditMode && !this.isUpdating) {
					this.id = value.id;
					this.name = value.name;
					this.duration = value.duration;
					this.sessionBased = value.sessionBased;
					this.status = value.status;

					this.startDate = value.startDate;
					this.startTime = value.startTime;
					this.endDate = value.endDate;
					this.endTime = value.endTime;

					this.desktopImage = value.desktopImage;
					this.mobileImage = value.mobileImage;
					this.campaignLink = value.link;
				}
			},
		},
	},

	methods: {
		...mapActions({
			deletePopupCampaign: 'onSiteMessages/deletePopupCampaign',
		}),

		handleChangeEndDate(value) {
			this.endDate = value;
		},

		async handleSubmit() {
			const generalRef = this.$refs.general.$v;
			const dateRef = this.$refs.date.$v;
			const contentRef = this.$refs.content.$v;
			const desktopImage = this.$refs.desktopImage.$v;
			const mobileImage = this.$refs.mobileImage.$v;

			generalRef.$touch();
			dateRef.$touch();
			contentRef.$touch();
			desktopImage.$touch();
			mobileImage.$touch();

			if (
				!generalRef.$invalid &&
				!dateRef.$invalid &&
				!contentRef.$invalid &&
				!desktopImage.$invalid &&
				!mobileImage.$invalid
			) {
				let data = {
					name: this.name,
					is_active: this.status,
					start_at: convertDateTimeToUTC(this.startDate, this.startTime),
					end_at: convertDateTimeToUTC(this.endDate, this.endTime),
					re_eligibility_duration_hours: Number(this.duration, 1),
					is_re_eligibility_session_based: this.sessionBased,
					link: this.campaignLink,
				};

				if (this.desktopImage?.id) {
					data = {
						...data,
						desktop_image_file_id: this.desktopImage?.id ?? null,
					};
				}

				if (this.mobileImage?.id) {
					data = {
						...data,
						mobile_image_file_id: this.mobileImage?.id ?? null,
					};
				}

				this.$emit('onSubmit', data);
			}
		},

		handleRemove() {
			this.$emit('onRemove');
		},
	},
};
</script>

<style lang="scss" scoped>
	.remove-banners {
		font-size: rem(14);
		color: $color-orange;

		align-items: center;

		cursor: pointer;
	}
</style>
